<template>
  <div
    ref="liteHero"
    v-editable="blok"
    class="lite-hero"
    :class="[animate && 'lite-hero--animate', useAsHeading && 'use-as-heading']"
  >
    <div class="lite-hero__content">
      <p v-if="blok.eyebrow" class="eyebrow" role="doc-subtitle">
        {{ blok.eyebrow }}
      </p>
      <CustomHeading
        :blok="blok.heading[0]"
        :class="blok.extend_heading_container && 'custom-heading--wide'"
      />
      <div class="lite-hero__text-cta">
        <RichTextEditor v-if="blok.text" :blok="{ text: blok.text }" />
        <CTAGroup
          v-if="blok.ctas && blok.text.content[0].content"
          class="thing"
          :ctas="blok.ctas"
        />
        <CTAGroup
          v-else-if="blok.ctas"
          class="lite-hero__right-CTA"
          :ctas="blok.ctas"
        />
        <RichTextEditor
          v-if="blok.legalText"
          class="mt-4"
          :blok="{ text: blok.legalText }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { LiteHeroBlokProps } from '@/types';

// Plugins
const { $noMotionPreference } = useNuxtApp();

// Props
const props = defineProps<{
  blok: LiteHeroBlokProps;
}>();

// Variables & refs
let observer: IntersectionObserver;
const animate = ref(false);
const useAsHeading = computed(() => props.blok.useAsHeading === true);
const liteHero = ref<HTMLDivElement>();

// Lifecycle : onMounted
onMounted(() => {
  // Only set up IO if no motion preference and there's an item to animate
  if ($noMotionPreference === true && liteHero.value) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting === true) {
          animate.value = true;
        }
      });
    });
    observer.observe(liteHero.value);
  }
});

// Lifecycle : onBeforeUnmount
onBeforeUnmount(() => {
  if (observer) observer.disconnect();
});
</script>

<style lang="scss">
.lite-hero {
  @include container;

  padding-block-start: var(--spacing-l);

  .custom-heading {
    @media all and #{$mq-s-max} {
      margin-block-end: var(--spacing-xs);
    }

    @media all and #{$mq-xs-min} {
      width: columns(10);
    }

    &--wide {
      @media all and #{$mq-xs-min} {
        width: columns(13);
      }
    }
  }

  &__content {
    margin-block-end: var(--spacing-m);

    @media all and #{$mq-xs-max} {
      max-width: columns(12);
    }

    /* Linting error is false positive */
    @media all and (min-width: #{$mq-xs-min-value}) and (max-width: #{$mq-s-max-value}) {
      max-width: columns(16);
    }

    @media all and #{$mq-m-min} {
      column-gap: columns(1);
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    }

    > .eyebrow {
      margin-block-end: var(--spacing-xs);

      @media all and #{$mq-m-min} {
        flex-basis: 100%;
      }
    }
  }

  &__text-cta {
    margin-inline-end: columns(0.25);
    position: relative;
    width: 100%;
    z-index: 2;

    @media all and #{$mq-m-min} {
      max-width: columns(7);
    }
  }

  .cta-group {
    margin-block-start: var(--spacing-s);
  }

  &__right-CTA {
    flex-direction: row-reverse;
    margin-block-start: 0 !important;
    margin-inline-end: 0 !important;
    @media all and #{$mq-s-max} {
      margin-block-start: var(--spacing-s) !important;
      flex-direction: row;
    }
  }
}

@media screen and (prefers-reduced-motion: no-preference) {
  .lite-hero {
    opacity: 0;
  }

  .lite-hero--animate {
    --timing-text-cta-fade-delay: calc(
      var(--timing-heading-wipe) + var(--timing-hero-start-delay)
    );
    --timing-large-image-delay: calc(
      var(--timing-text-cta-fade-delay) + var(--timing-text-cta-fade)
    );
    --timing-small-image-delay: calc(
      var(--timing-large-image-delay) + var(--timing-large-image-fade)
    );

    opacity: 1;
    transition: opacity 0ms linear var(--timing-hero-start-delay);

    .eyebrow {
      animation: fade-in var(--timing-eyebrow) var(--easing-3) forwards;
      animation-delay: var(--timing-hero-start-delay);
    }

    .custom-heading {
      animation: wipe-left var(--timing-heading-wipe) var(--easing-2),
        scale var(--timing-heading-scale) var(--easing-2),
        fade-in var(--timing-heading-fade) var(--easing-3);
      animation-delay: var(--timing-hero-start-delay);
      animation-fill-mode: forwards;
      will-change: clip-path, transform, opacity;
    }

    .lite-hero__text-cta {
      animation: fade-in var(--timing-text-cta-fade) var(--easing-3)
        var(--timing-text-cta-fade-delay) forwards;
      opacity: 0;
      will-change: opacity;
    }
  }
}

.use-as-heading {
  padding-block-end: var(--spacing-m);
}
</style>
